import { z } from 'zod';
import { normalizeHex } from '../colors.js';
/* ═══════════════════════════════════════════════════════════════════════
 * TAGS — the one way a student is labelled.
 *
 * A tag is a lower-cased word Diana puts on a student: "premium", "low",
 * "speak and roll". It filters the clients list, it scopes the whole
 * accounting page, and — when it is given a colour — it paints that
 * student's lessons in the calendar.
 *
 * There used to be a second concept, "groups", for the schools she teaches
 * for. It was the same shape with a colour attached, so it was folded in
 * here: colouring the tag `speak and roll` now does what a group did, and
 * the same label answers "how much did I earn from them" as well.
 *
 * ORDER IS LOAD-BEARING. A lesson tile has one background, so the FIRST tag
 * in a student's list that has a colour is the one that paints it. That is
 * why the list de-duplicates while keeping the earliest position, and why
 * the tag editor must preserve the order she typed.
 * ═══════════════════════════════════════════════════════════════════════ */
/**
 * Stored lower-cased as '#rrggbb'. Anything a colour input or a paste can
 * produce is accepted and normalised, so '#ABC' and 'aabbcc' are the same
 * colour rather than two values that look identical and compare unequal.
 */
export const hexColorSchema = z
    .string()
    .trim()
    .transform((value, ctx) => {
    const hex = normalizeHex(value);
    if (!hex) {
        ctx.addIssue({ code: 'custom', message: 'Podaj kolor w formacie #RRGGBB' });
        return z.NEVER;
    }
    return hex;
});
/** Same, but an empty string survives — it means "no colour of its own". */
export const optionalHexColorSchema = z
    .string()
    .trim()
    .max(9)
    .transform((value, ctx) => {
    if (value === '')
        return '';
    const hex = normalizeHex(value);
    if (!hex) {
        ctx.addIssue({ code: 'custom', message: 'Podaj kolor w formacie #RRGGBB' });
        return z.NEVER;
    }
    return hex;
});
/**
 * One tag.
 *
 * Commas are refused because the tag editor is a comma-separated field: a
 * tag containing one would silently split in two the next time that student
 * is saved, and its colour would come off.
 */
export const tagSchema = z
    .string()
    .trim()
    .min(1, 'Tag nie może być pusty')
    .max(40, 'Tag może mieć najwyżej 40 znaków')
    .refine((t) => !t.includes(','), 'Tag nie może zawierać przecinka')
    .transform((t) => t.toLowerCase());
/**
 * A student's tags: de-duplicated, keeping the EARLIEST position of each,
 * because position decides which colour the lesson takes.
 */
export const tagListSchema = z
    .array(tagSchema)
    .max(20)
    .transform((tags) => [...new Set(tags)]);
/** Giving a tag a colour. An empty colour removes it; the tag itself stays. */
export const tagStyleInputSchema = z.object({
    tag: tagSchema,
    color: optionalHexColorSchema,
});
export const tagStyleSchema = z.object({
    tag: z.string(),
    color: z.string(),
});
export const tagRenameSchema = z.object({
    from: tagSchema,
    to: tagSchema,
});
/**
 * One student's tag list after a rename.
 *
 * Renaming onto a tag the student already carries merges the two into a
 * single entry at the earlier position, rather than leaving a duplicate that
 * would show twice and confuse the "first coloured tag" rule.
 */
export function renameTagIn(tags, from, to) {
    return [...new Set(tags.map((tag) => (tag === from ? to : tag)))];
}
